import {VALIDATORS} from '@github-ui/entity-validators'
import {useSessionStorage} from '@github-ui/use-safe-storage/session-storage'
import {ActionList, ActionMenu, Tooltip} from '@primer/react'
import React, {useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react'
import {graphql, useFragment} from 'react-relay'

import type {GraphqlFragment, IssueFormElementRef, SharedElementProps} from '../types'
import {safeGetDefaultValue} from '../utils/default-value'
import {prefixMarkdownTitle} from '../utils/markdown-conversion'
import {uniqueIdForElementInternal} from '../utils/session-storage'
import type {DropdownElement_input$data, DropdownElement_input$key} from './__generated__/DropdownElement_input.graphql'
import styles from './DropdownElement.module.css'
import {ElementWrapper} from './ElementWrapper'

const DEFAULT_CHOICE = 'None'

/**
 * Parses a comma-separated string where each value is quoted and internal quotes are escaped with backslash.
 * Uses JSON.parse() for robust parsing by converting to a proper JSON array format.
 * Example: "\"Value 1\", \"Value with \\\"quotes\\\" and, comma\", \"Value 3\"" -> ["Value 1", "Value with \"quotes\" and, comma", "Value 3"]
 * The format is defined by prompt in SkillWithIssueForms skill in CAPI
 */
export function parseModelDropdownValues(valuesString: string): string[] {
  if (!valuesString.trim()) return []

  try {
    // Convert comma-separated quoted values to JSON array format by wrapping in brackets
    const jsonString = `[${valuesString}]`
    return JSON.parse(jsonString) as string[]
  } catch {
    // If JSON parsing fails, return empty array (malformed input)
    return []
  }
}

/**
 * Escapes values for JSON-like format by quoting values and escaping internal backslashes and quotes.
 * Example: ["Value 1", "Value with \"quotes\" and, comma", "Path\\to\\file"] -> "\"Value 1\", \"Value with \\\"quotes\\\" and, comma\", \"Path\\\\to\\\\file\""
 */
export function escapeDropdownValues(values: string[]): string {
  return values
    .map(value => {
      // Escape backslashes first, then quotes, and wrap in quotes
      return `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`
    })
    .join(', ')
}

/**
 * Extracts default values from defaultValuesById and filters them against valid option choices.
 * Returns an array of valid option strings ordered by their position in optionChoices.
 */
export function getValidDefaultChoices({
  itemId,
  indexOfFormElement,
  defaultValuesById,
  optionChoices,
}: {
  itemId: string | null | undefined
  indexOfFormElement?: number
  defaultValuesById: SharedElementProps['defaultValuesById']
  optionChoices: readonly string[]
}): string[] {
  if (!itemId) return []
  const defaultValueString = safeGetDefaultValue({id: itemId, indexOfFormElement, value: '', defaultValuesById})
  const defaultValues = new Set(parseModelDropdownValues(defaultValueString))
  // Filtering optionChoices instead of returning defaultValues preserves the order of options.
  return optionChoices.filter(choice => defaultValues.has(choice))
}

export type DropdownElementInternalProps = GraphqlFragment<DropdownElement_input$data> & {
  type: 'dropdown'
  index?: number
}

export const DropdownElement = ({
  ref,
  elementRef,
  index,
  sessionStorageKey,
  defaultValuesById,
  onFieldChange,
}: {
  elementRef: DropdownElement_input$key
  index?: number
  ref?: React.Ref<IssueFormElementRef>
} & SharedElementProps) => {
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment DropdownElement_input on IssueFormElementDropdown {
        __id
        itemId: id
        label
        descriptionHTML
        options
        required
        multiple
        defaultOptionIndex: default
      }
    `,
    elementRef,
  )

  if (!data.options) return null
  const elementStorageKey = uniqueIdForElementInternal(sessionStorageKey, '', data.label, data.__id)
  return (
    <DropdownElementInternal
      {...data}
      index={index}
      ref={ref}
      type={'dropdown'}
      sessionStorageKey={elementStorageKey}
      defaultValuesById={defaultValuesById}
      onFieldChange={onFieldChange}
    />
  )
}

export const DropdownElementInternal = ({
  ref,
  itemId,
  index: indexOfFormElement,
  sessionStorageKey,
  label,
  options,
  descriptionHTML,
  required,
  multiple,
  defaultOptionIndex,
  defaultValuesById,
  onFieldChange,
}: DropdownElementInternalProps & SharedElementProps & {ref?: React.Ref<IssueFormElementRef>}) => {
  const anchorRef = useRef<HTMLButtonElement>(null)
  const buttonTextRef = useRef<HTMLSpanElement>(null)
  const hasDefaultValue = defaultOptionIndex !== undefined && defaultOptionIndex !== null
  // We want to prefix the default `None` option if it's singular, not required, and doesn't have a default option.
  const optionChoices = useMemo(
    () => (multiple || required || hasDefaultValue ? options : [DEFAULT_CHOICE, ...options]),
    [hasDefaultValue, multiple, options, required],
  )

  // use the provided value in defaultValuesById. If no default value and dropdown is a single select,
  // choose the first option as the default
  const defaultSelectionValue = useMemo(() => {
    // extract default values from defaultValuesById. Cross-reference with optionChoices to ensure valid choices.
    let defaultSelection = getValidDefaultChoices({
      itemId,
      indexOfFormElement,
      defaultValuesById,
      optionChoices,
    })

    // if there were no defaults in defaultValuesById, but defaultOptionIndex was provided, use that
    if (
      defaultSelection.length === 0 &&
      hasDefaultValue &&
      defaultOptionIndex >= 0 &&
      defaultOptionIndex < optionChoices.length
    ) {
      return [optionChoices[defaultOptionIndex]!]
    }

    // if no default provided and single select, choose the first option:
    if (defaultSelection.length === 0 && optionChoices.length > 0 && !multiple) {
      defaultSelection = [optionChoices[0]!]
    }

    return defaultSelection
  }, [defaultOptionIndex, defaultValuesById, hasDefaultValue, indexOfFormElement, itemId, multiple, optionChoices])

  const [selectedItems, setSelectedItems] = useSessionStorage<string[]>(sessionStorageKey, defaultSelectionValue)

  const latestSelectedItemsRef = useRef(selectedItems)

  // Keep ref updated with latest selectedItems
  useEffect(() => {
    latestSelectedItemsRef.current = selectedItems
  }, [selectedItems])

  // Updates selected items with new default values from defaultValuesById
  useEffect(() => {
    if (onFieldChange && defaultValuesById) {
      const defaultSelection = getValidDefaultChoices({
        itemId,
        indexOfFormElement,
        defaultValuesById,
        optionChoices,
      })

      setSelectedItems(defaultSelection)
      latestSelectedItemsRef.current = defaultSelection
    }
  }, [defaultValuesById, indexOfFormElement, itemId, optionChoices, setSelectedItems, onFieldChange])

  // Sends out user changes to selectedItems using the ref for latest value
  useEffect(() => {
    if (onFieldChange) {
      const selectedItemsArray = latestSelectedItemsRef.current
      const defaultValuesArray = getValidDefaultChoices({
        itemId,
        indexOfFormElement,
        defaultValuesById,
        optionChoices,
      })

      // Compare arrays using Sets for accurate comparison regardless of order
      const selectedItemsSet = new Set(selectedItemsArray)
      const defaultValuesSet = new Set(defaultValuesArray)

      const arraysEqual =
        selectedItemsSet.size === defaultValuesSet.size &&
        [...selectedItemsSet].every(item => defaultValuesSet.has(item))

      if (!arraysEqual) {
        onFieldChange(itemId, indexOfFormElement, escapeDropdownValues(selectedItemsArray))
      }
    }
  }, [selectedItems, itemId, indexOfFormElement, onFieldChange, defaultValuesById, optionChoices])

  const [validationResult, setValidationResult] = useState<string | undefined>(undefined)

  const toggleSelection = useCallback(
    (option: string) => () => {
      if (selectedItems.includes(option)) {
        // We only allow deselection of the active item if we have multiple select.
        if (multiple) {
          setSelectedItems(selectedItems.filter(item => item !== option))
        }
      } else {
        if (multiple) {
          setSelectedItems([...selectedItems, option])
        } else {
          setSelectedItems([option])
        }
      }
    },
    [multiple, selectedItems, setSelectedItems],
  )

  useImperativeHandle(
    ref,
    () => ({
      focus: () => anchorRef.current?.focus(),
      markdown: () => prefixMarkdownTitle(label, selectedItems.join(', ')),
      validate: () => {
        if (required && selectedItems.length === 0) {
          setValidationResult(VALIDATORS.missingDropdownSelection)
          return false
        }
        return true
      },
      reset: () => {
        setSelectedItems(defaultSelectionValue)
        setValidationResult(undefined)
      },
      hasChanges: () => {
        if (selectedItems.length !== defaultSelectionValue.length) {
          return true
        }

        for (const item of selectedItems) {
          if (!defaultSelectionValue.includes(item)) {
            return true
          }
        }

        return false
      },
      getSessionStorageKey: () => sessionStorageKey,
      type: 'dropdown' as const,
    }),
    [defaultSelectionValue, label, required, selectedItems, sessionStorageKey, setSelectedItems],
  )

  useEffect(() => {
    if (selectedItems.length > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setValidationResult(undefined)
    }
  }, [selectedItems])

  const [isTruncated, setIsTruncated] = useState(false)

  const buttonText = selectedItems.length === 0 ? 'None' : selectedItems.join(', ')

  // Only show tooltip if the text is truncated.
  // This is not bullet proof, since the true inner width of the element is rounded down.
  // I think it's pretty good though. See discussion here https://stackoverflow.com/questions/64689074/how-to-check-if-text-is-truncated-by-css-using-javascript
  useEffect(() => {
    if (!buttonTextRef.current) return
    const truncate = buttonTextRef.current.scrollWidth > buttonTextRef.current.clientWidth
    setIsTruncated(truncate)
  }, [buttonTextRef, setIsTruncated])

  return (
    <ElementWrapper
      label={label}
      description={descriptionHTML}
      required={required}
      validationResult={validationResult}
      className={styles.dropdownElementContainer}
    >
      {({labelId, descriptionIds}) => (
        <ActionMenu anchorRef={anchorRef}>
          {isTruncated ? (
            <Tooltip text={buttonText}>
              <ActionMenu.Button aria-labelledby={labelId} aria-describedby={descriptionIds}>
                <span ref={buttonTextRef} className={styles.dropdownElementButtonText}>
                  {buttonText}
                </span>
              </ActionMenu.Button>
            </Tooltip>
          ) : (
            <ActionMenu.Button aria-labelledby={labelId} aria-describedby={descriptionIds}>
              {' '}
              <span ref={buttonTextRef} className={styles.dropdownElementButtonText}>
                {buttonText}
              </span>
            </ActionMenu.Button>
          )}
          <ActionMenu.Overlay
            width="medium"
            side="outside-top"
            maxHeight="medium"
            className={styles.dropdownMenuOverlay}
          >
            <ActionList selectionVariant={multiple ? 'multiple' : 'single'}>
              {optionChoices.map((option, index) => (
                <ActionList.Item
                  // eslint-disable-next-line @eslint-react/no-array-index-key
                  key={index}
                  selected={selectedItems.includes(option)}
                  onSelect={toggleSelection(option)}
                >
                  {option}
                </ActionList.Item>
              ))}
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      )}
    </ElementWrapper>
  )
}

DropdownElement.displayName = 'DropdownElement'
DropdownElementInternal.displayName = 'DropdownElementInternal'
